/**
 * A field of drifting embers: markup only, the motion is CSS (`.bv-embers` in
 * `styles/arena.css.ts`), so reduced-motion can stop it and nothing ticks.
 *
 * Positions come from a fixed sequence rather than Math.random so the field
 * is the same every time it is drawn, and a test or a screenshot of it is
 * stable. Each ember carries its own lane, size, delay and duration as custom
 * properties; the keyframes read those, so one rule animates them all.
 */
export function embersHtml(count: number, extraClass = ''): string {
  const spans: string[] = [];
  for (let i = 0; i < count; i += 1) {
    // Golden-ratio steps spread the lanes evenly without visible columns.
    const lane = ((i * 0.618034) % 1) * 100;
    const size = 2 + ((i * 7) % 4);
    const delay = -((i * 1.37) % 6);
    const dur = 4.5 + ((i * 2.3) % 4);
    const sway = ((i % 2 === 0 ? 1 : -1) * (10 + ((i * 13) % 30)));
    spans.push(
      `<i style="--x:${lane.toFixed(1)}%;--s:${size}px;--d:${delay.toFixed(2)}s;--t:${dur.toFixed(2)}s;--w:${sway}px"></i>`,
    );
  }
  return `<span class="bv-embers ${extraClass}" aria-hidden="true">${spans.join('')}</span>`;
}

/**
 * Starts the browser fetching an image before the element that shows it is
 * displayed. Chrome does not load a background for a display:none box, so a
 * card revealed at the decisive moment would otherwise pop in with an empty
 * backdrop. Fire-and-forget; the HTTP cache does the rest.
 */
export function preloadImages(urls: readonly string[]): void {
  for (const url of urls) {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
  }
}

/** The phone tier of an arena backdrop below this width — matches the CSS media query. */
export const ARENA_ART_SMALL_MAX_PX = 720;

/** `/ui/arena/<name>.webp`, or its `-sm` tier on a narrow screen. */
export function arenaArtUrl(name: 'versus' | 'victory' | 'defeat' | 'draw'): string {
  const small = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia(`(max-width: ${ARENA_ART_SMALL_MAX_PX}px)`).matches;
  return `/ui/arena/${name}${small ? '-sm' : ''}.webp`;
}
